import type { StoryTraceStepSummaryDto } from '@terra/shared-contracts-smoke-test-history';
import type { TraceMessage } from '../types';

export const STORY_SUMMARY_STEP_KEY = 'summary';

export type StoryStepSource = 'neo' | 'co-pilot';

export type ResolvedStoryStepMeta = {
  stepKey: string;
  source: StoryStepSource;
  visible: boolean;
};

const isStoryStepSource = (value: unknown): value is StoryStepSource =>
  value === 'neo' || value === 'co-pilot';

export const resolveStoryStepMetaFromMessage = ({
  message,
  steps,
}: {
  message: TraceMessage;
  steps?: StoryTraceStepSummaryDto[];
}): ResolvedStoryStepMeta | null => {
  if (message.id.endsWith('-summary')) {
    return {
      stepKey: STORY_SUMMARY_STEP_KEY,
      source: 'co-pilot',
      visible: true,
    };
  }

  let stepKeyFromMeta: string | undefined;
  let sourceFromMeta: StoryStepSource | undefined;
  let visibleFromMeta: boolean | undefined;

  for (const content of message.content) {
    if (content.type !== 'tool_call') {
      continue;
    }

    const metadata = content.toolCall.metadata;
    const stepKey =
      typeof metadata?.storyStepKey === 'string'
        ? metadata.storyStepKey
        : undefined;

    if (!stepKey) {
      continue;
    }

    stepKeyFromMeta = stepKey;
    if (isStoryStepSource(metadata?.storyStepSource)) {
      sourceFromMeta = metadata.storyStepSource;
    }

    if (typeof metadata?.storyStepVisible === 'boolean') {
      visibleFromMeta = metadata.storyStepVisible;
    }

    break;
  }

  const stepKeyFromId = message.id.includes('-step-')
    ? message.id.slice(message.id.lastIndexOf('-step-') + '-step-'.length)
    : null;
  const stepKey = stepKeyFromMeta ?? stepKeyFromId;

  if (!stepKey) {
    return null;
  }

  const matched = steps?.find((step) => step.stepKey === stepKey);

  if (matched) {
    return {
      stepKey: matched.stepKey,
      source: matched.source,
      visible: matched.visible,
    };
  }

  if (
    stepKeyFromMeta &&
    sourceFromMeta &&
    typeof visibleFromMeta === 'boolean'
  ) {
    return {
      stepKey: stepKeyFromMeta,
      source: sourceFromMeta,
      visible: visibleFromMeta,
    };
  }

  return null;
};
